import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ShoppingBasket, CarFront, HeartHandshake, HandHelping, HandHeart } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { TrustBadge } from "@/components/TrustBadge";
import { CATEGORIES, STATUS_LABEL } from "@/lib/api";

const CAT_ICON = { groceries: ShoppingBasket, transport: CarFront, companionship: HeartHandshake };
const STATUS_STYLE = {
  open: "bg-forest-700 text-white",
  connected: "bg-gold text-forest-900",
  in_progress: "bg-terracotta text-white",
  completed: "bg-stone-200 text-forest-700",
};

export function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export function PostCard({ post }) {
  const navigate = useNavigate();
  const cats = post.categories && post.categories.length ? post.categories : [post.category];
  const Icon = CAT_ICON[cats[0]] || HeartHandshake;
  const catLabel = cats.map((c) => CATEGORIES[c]?.label).filter(Boolean).join(" · ");
  const isOffer = post.kind === "offer";
  return (
    <div
      data-testid={`post-card-${post.post_id}`}
      onClick={() => navigate(`/app/post/${post.post_id}`)}
      className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 cursor-pointer transition-transform hover:-translate-y-1 hover:shadow-md animate-rise"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold ${isOffer ? "bg-forest-700 text-white" : "bg-terracotta text-white"}`}>
          {isOffer ? <HandHeart size={16} strokeWidth={2.5} /> : <HandHelping size={16} strokeWidth={2.5} />}
          {isOffer ? "Offering help" : "Needs help"}
        </span>
        <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[post.status]}`}>
          {STATUS_LABEL[post.status]}
        </span>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-cream border-2 border-border flex items-center justify-center shrink-0">
          <Icon size={24} strokeWidth={2.5} className="text-forest-700" />
        </div>
        <div className="min-w-0">
          <h3 className="font-head font-bold text-xl text-forest-900 leading-snug">{post.title}</h3>
          <p className="text-sm text-forest-500 font-medium mt-0.5">{catLabel}</p>
        </div>
      </div>
      <p className="text-forest-700 mt-3 line-clamp-2 leading-relaxed">{post.description}</p>
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border">
        <Avatar user={{ name: post.author_name, picture: post.author_picture }} size={36} />
        <span className="font-semibold text-forest-900">{post.author_name}</span>
        {post.author_trust && post.author_trust !== "unverified" && <TrustBadge level={post.author_trust} size="sm" showLabel={false} />}
        <span className="text-forest-500 text-sm ml-auto flex items-center gap-3">
          {post.distance_km != null && (
            <span className="flex items-center gap-1"><MapPin size={15} strokeWidth={2.5} />{post.distance_km} km</span>
          )}
          {timeAgo(post.created_at)}
        </span>
      </div>
    </div>
  );
}
